import { isNil } from 'lodash';
import { useContext, useMemo } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { ShareQRCode } from '@shared/components/qrCodes';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { usePermission } from '@shared/util/permissions/hooks';
import { useCardDefinition } from '../../hooks';
import {
  copyToClipboard,
  useHandleCopyQr,
  useSharedCardLinkState,
} from '../hooks/ShareQrCodeOperations';
import styles from './ShareSheet.module.css';

interface Props {
  anchorName: string;
  card?: { id: string; workflowTemplateId: string | null };
  onCloseSheet(): void;
}

export function ShareSheet({ anchorName, card, onCloseSheet }: Props) {
  const channel = useContext(CardChannelContext);
  const sharedCardLinkPermissions = usePermission({
    action: 'manage',
    subject: 'SharedCardLink',
    channelId: channel?.id,
  });
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const nocodeReadonlyPublicTemplate = cardDefinition?.templates?.find(uiTemplate => {
    return uiTemplate.template.context === 'nocode-public-readonly';
  });

  const cardHost = getCardHost();
  const privateCardUrl = useMemo(() => {
    return `${cardHost}/${channel?.slug}/cards/${card?.id}`;
  }, [cardHost, channel?.slug, card?.id]);

  const { showPublicQrCode, publicLink, onToggleShowPublicQrCode } = useSharedCardLinkState({
    channelId: channel?.id,
    type: 'VIEW_CARD',
    viewTemplateId: nocodeReadonlyPublicTemplate?.template?.slug,
    cardId: card?.id,
    disabled: sharedCardLinkPermissions.enabled,
  });

  const copyQr = useHandleCopyQr();

  const renderTitleBar = () => {
    return (
      <AnchoredSheetTitleBar
        rightIcon="exit"
        rightIconSize={grid(4)}
        onRightIconPress={onCloseSheet}
        title={i18n('qrCode.share.shareCardTitle')}
      />
    );
  };

  const isLowCodeCard = isNil(card?.workflowTemplateId);
  const hasNoCodeReadonlyPublicTemplate =
    !isNil(card?.workflowTemplateId) && !isNil(nocodeReadonlyPublicTemplate);

  return (
    <AnchoredSheet
      anchorName={anchorName}
      initialSize="full"
      renderTitleBar={renderTitleBar}
      onClose={onCloseSheet}
      className={styles.shareSheet}
      testID="share-card-sheet"
    >
      <View paddingBottom={1}>
        <ShareQRCode
          privateShare={{
            url: privateCardUrl,
            onCopyQr: copyQr,
            onCopyLink: copyToClipboard,
          }}
          canCreatePublicCode={
            sharedCardLinkPermissions.enabled && (isLowCodeCard || hasNoCodeReadonlyPublicTemplate)
          }
          hasPublicQrCode={showPublicQrCode}
          publicUrl={publicLink?.url}
          onSwitchPublic={onToggleShowPublicQrCode}
          onCopyPublicQr={copyQr}
          onCopyPublicLink={copyToClipboard}
        />
      </View>
    </AnchoredSheet>
  );
}

function getCardHost() {
  const appUrl = import.meta.env.VITE_APP_URL;
  if (import.meta.env.DEV && appUrl.includes('localhost')) {
    return 'https://staging.coastapp.com';
  } else {
    return appUrl;
  }
}
